import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import { BrowserRouter as Router, Link, Redirect, Route, useParams } from "react-router-dom";
import "../css/Admin_css/admin-customize.css";
import OldDueCollection from "./OldDueCollection";
import myToast from "../Utils/MyToast";
import SelectClass from "../ChildComponent/SelectClass";
import AddOldDue from "./AddOldDue";
import BackButton from "../ChildComponent/BackButton";

// Reusable style constants
const cardStyle = {
  borderRadius: "12px",
  padding: "16px",
  boxShadow: `0 2px 8px ${COLORS.primary_color_shade3}20`,
  transition: "all 0.3s ease",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
};

const amountCardStyle = {
  padding: "8px 14px",
  borderRadius: "8px",
  color: COLORS.white,
  textAlign: "center",
  minWidth: "110px",
  boxShadow: `0 2px 6px rgba(0,0,0,0.3)`,
};

const OldDueListContent = () => {
  const { tag } = useParams();
  const [loggedIn, setLoggedIn] = useState(true);
  const [Loading, setLoading] = useState(false);
  const [LoadingStudent, setLoadingStudent] = useState(false);
  const [mOldDueList, setOldDueList] = useState([]);
  const [mFilteredOldDueList, setFilteredOldDueList] = useState([]);
  const [classId, setClassId] = useState(null);
  const [sectionId, setSectionId] = useState(null);
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoggedIn(false);
    }
  }, []);

  // Memoized calculation functions
  const calculateStudentTotals = useCallback((oldDueArray) => {
    if (!oldDueArray || !Array.isArray(oldDueArray) || oldDueArray.length === 0) {
      return { totalDue: 0, totalPaid: 0, totalSettled: 0 };
    }

    return oldDueArray.reduce(
      (acc, item) => {
        acc.totalDue += Number(item.amount) || 0;
        acc.totalPaid += Number(item.amount_paid) || 0;
        acc.totalSettled += Number(item.settled_amount) || 0;
        return acc;
      },
      { totalDue: 0, totalPaid: 0, totalSettled: 0 }
    );
  }, []);

  // Memoize student calculations to avoid recalculating on every render
  const studentCalculations = useMemo(() => {
    return mFilteredOldDueList.map((item) => {
      const totals = calculateStudentTotals(item.old_due);
      const currentDue = Math.max(0, totals.totalDue - totals.totalSettled - totals.totalPaid);
      return {
        ...item,
        ...totals,
        currentDue,
        classDetails: item.class?.[0] || {},
      };
    });
  }, [mFilteredOldDueList, calculateStudentTotals]);

  // Calculate total stats from memoized calculations
  const totalStats = useMemo(() => {
    const stats = studentCalculations.reduce(
      (acc, student) => {
        acc.totalDue += student.totalDue;
        acc.totalPaid += student.totalPaid;
        acc.totalSettled += student.totalSettled;
        return acc;
      },
      { totalDue: 0, totalPaid: 0, totalSettled: 0 }
    );
    stats.currentDue = Math.max(0, stats.totalDue - stats.totalSettled - stats.totalPaid);
    return stats;
  }, [studentCalculations]);

  const CallBackClassSelect = useCallback(
    (class_id, section_id, session_id) => {
      setClassId(class_id);
      setSectionId(section_id);
      setSessionId(session_id);
      getOldDueList(class_id, section_id, session_id);
    },
    []
  );

  const getOldDueList = useCallback((class_id, section_id, session_id) => {
    if (!class_id || !section_id || !session_id) return;

    setLoadingStudent(true);
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const FUNCTION_NAME = "getClassStudentOldDueList.php";
    const URL =
      process.env.REACT_APP_SERVER_NAME +
      process.env.REACT_APP_TARGET_DIR +
      FUNCTION_NAME;

    const params = {
      school_code: SCHOOL_CODE,
      class_id,
      section_id,
      session_id,
    };

    // Build full URL with parameters for logging
    const queryString = new URLSearchParams(params).toString();
    const fullURL = URL + queryString;
    console.log("getOldDueList - Full URL:", fullURL);
    console.log("getOldDueList - Params:", params);

    axios
      .get(URL, { params })
      .then((res) => res.data)
      .then((response) => {
        setLoadingStudent(false);
        if (response.api_status === "OK") {
          setOldDueList(response.data || []);
          getFilteredList(response.data || []);
        } else {
          myToast.notify_warning(response.api_remark || "Failed to load old due list");
          setOldDueList([]);
          getFilteredList([]);
        }
      })
      .catch((error) => {
        setLoadingStudent(false);
        console.error("Error fetching old due list:", error);
        myToast.notify_error("Error loading old due list");
        setOldDueList([]);
        getFilteredList([]);
      });
  }, []);

  const getFilteredList = useCallback((list) => {
    const filtered = (list || mOldDueList).filter((item) => {
      if (!item.old_due || item.old_due === null || item.old_due === undefined) {
        return false;
      }
      if (Array.isArray(item.old_due) && item.old_due.length === 0) {
        return false;
      }
      if (typeof item.old_due === "string" && item.old_due.toLowerCase() === "null") {
        return false;
      }
      return true;
    });
    console.log("Filtered list:", filtered.length, "out of", (list || mOldDueList).length);
    setFilteredOldDueList(filtered);
  }, [mOldDueList]);

  const getTotalAmountText = useCallback(() => {
    if (tag === "1") {
      return `Total Due: ₹${totalStats.totalDue.toLocaleString()}`;
    } else if (tag === "2") {
      return `Total Received: ₹${totalStats.totalPaid.toLocaleString()}`;
    } else if (tag === "3") {
      return `Current Old Due: ₹${totalStats.currentDue.toLocaleString()}`;
    }
    return `Current Old Due: ₹${totalStats.currentDue.toLocaleString()}`;
  }, [tag, totalStats]);

  const saveData = useCallback((item) => {
    localStorage.setItem("selected_student", JSON.stringify(item));
  }, []);

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  if (Loading) {
    return <MyLoader />;
  }

  return (
    <div className="content">
      <div className="content-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i className="fa fa-home" aria-hidden="true" />
              <a href="#">Old Due List</a>
            </li>
          </ul>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <BackButton />
        </div>
      </div>

      <div
        className="bg_page_white_rounded box_shadow animated fadeInUp"
        style={{ "--mMargin": "30px", padding: "24px" }}
      >
        <SelectClass action={CallBackClassSelect} selected_position="0" />

        {/* Total Amount Card */}
        {mFilteredOldDueList.length > 0 && (
          <div
            style={{
              marginTop: "24px",
              padding: "20px",
              background: `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade6} 100%)`,
              borderRadius: "12px",
              border: `2px solid ${COLORS.primary_color_shade6}`,
              boxShadow: `0 4px 16px ${COLORS.primary_color_shade3}40`,
              textAlign: "center",
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: "24px",
                fontWeight: "700",
                color: COLORS.white,
              }}
            >
              {getTotalAmountText()}
            </h3>
          </div>
        )}

        {/* Student List Section */}
        <section style={{ marginTop: "24px" }}>
          <h4
            style={{
              marginBottom: "20px",
              fontSize: "18px",
              fontWeight: "600",
              color: COLORS.black8 || "#333333",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <i className="fa fa-users" style={{ color: COLORS.primary_color_shade6 }} />
            Students with Old Due ({mFilteredOldDueList.length})
          </h4>

          {LoadingStudent ? (
            <MyLoader />
          ) : mFilteredOldDueList.length === 0 ? (
            <div
              style={{
                padding: "40px",
                textAlign: "center",
                background: COLORS.white,
                borderRadius: "12px",
                border: `2px solid ${COLORS.grey3 || "#E0E0E0"}`,
              }}
            >
              <i
                className="fa fa-inbox"
                style={{
                  fontSize: "48px",
                  color: COLORS.grey4 || "#BDBDBD",
                  marginBottom: "16px",
                }}
              />
              <p style={{ fontSize: "16px", color: COLORS.black6 || "#666666", margin: 0 }}>
                {classId
                  ? "No students with old due found for this class."
                  : "Please select a class to view old due list."}
              </p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {studentCalculations.map((student, index) => {
                const { totalDue, totalPaid, totalSettled, currentDue, classDetails } = student;
                const isSettled = currentDue === 0;
                const borderColor = isSettled ? COLORS.green3 : COLORS.primary_color_shade3 || "#4FC3F7";

                return (
                  <Link
                    key={student.student_enrollment_no || index}
                    to={`/AdminMain/OldDueCollection/${student.student_enrollment_no}/${classDetails.session_id || sessionId}`}
                    onClick={() => saveData(student)}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <div
                      style={{
                        ...cardStyle,
                        background: COLORS.white,
                        border: `2px solid ${borderColor}`,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow = isSettled
                          ? `0 4px 12px ${COLORS.green2}40`
                          : `0 6px 16px ${COLORS.primary_color_shade3}50`;
                        e.currentTarget.style.borderColor = isSettled
                          ? COLORS.green3
                          : COLORS.primary_color_shade6;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "";
                        e.currentTarget.style.boxShadow = cardStyle.boxShadow;
                        e.currentTarget.style.borderColor = borderColor;
                      }}
                    >
                      {/* Decorative background element */}
                      <div
                        style={{
                          position: "absolute",
                          top: "-30px",
                          right: "-30px",
                          width: "120px",
                          height: "120px",
                          background: isSettled
                            ? "rgba(56, 181, 3, 0.05)"
                            : "rgba(11, 181, 244, 0.05)",
                          borderRadius: "50%",
                          filter: "blur(30px)",
                        }}
                      />

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "16px",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        {/* Student Image */}
                        <StudentImage student={student} isSettled={isSettled} />

                        {/* Student Details */}
                        <StudentDetails student={student} classDetails={classDetails} />

                        {/* Amount Cards */}
                        <AmountCards
                          totalDue={totalDue}
                          totalSettled={totalSettled}
                          totalPaid={totalPaid}
                          currentDue={currentDue}
                          isSettled={isSettled}
                        />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* Add More Button */}
        <AddMoreButton />
      </div>
    </div>
  );
};

// Extracted components for better organization
const StudentImage = React.memo(({ student, isSettled }) => {
  const imageBorderColor = isSettled ? COLORS.green3 : COLORS.primary_color_shade6 || "#039BE5";
  const badgeBgColor = isSettled ? COLORS.green3 : COLORS.primary_color_shade6 || "#039BE5";

  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      <div
        style={{
          width: "70px",
          height: "70px",
          borderRadius: "50%",
          border: `3px solid ${imageBorderColor}`,
          padding: "3px",
          background: COLORS.white,
          boxShadow: `0 2px 8px ${COLORS.primary_color_shade3}40`,
          overflow: "hidden",
        }}
      >
        <img
          src={student.student_img_path || ""}
          alt={student.stu_name || "Student"}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "50%",
          }}
          onError={(e) => {
            e.target.style.display = "none";
            if (!e.target.nextSibling) {
              const fallback = document.createElement("div");
              fallback.style.cssText = `
                width: 100%;
                height: 100%;
                border-radius: 50%;
                background: linear-gradient(135deg, ${COLORS.primary_color_shade6} 0%, ${COLORS.primary_color_shade4} 100%);
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                font-size: 24px;
                font-weight: 700;
              `;
              fallback.textContent = student.stu_name?.charAt(0)?.toUpperCase() || "S";
              e.target.parentNode.appendChild(fallback);
            }
          }}
        />
      </div>
      <div
        style={{
          position: "absolute",
          bottom: "-2px",
          right: "-2px",
          background: badgeBgColor,
          color: COLORS.white,
          padding: "3px 6px",
          borderRadius: "8px",
          fontSize: "9px",
          fontWeight: "600",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
          border: "2px solid #fff",
        }}
      >
        {student.student_enrollment_no || ""}
      </div>
    </div>
  );
});

const StudentDetails = React.memo(({ student, classDetails }) => {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <h5
        style={{
          margin: "0 0 6px 0",
          fontSize: "16px",
          fontWeight: "700",
          color: COLORS.black8 || "#333333",
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <i
          className="fa fa-user"
          style={{ color: COLORS.primary_color_shade6, fontSize: "14px" }}
        />
        {student.stu_name || ""} {student.father_name ? `D/o ${student.father_name}` : ""}
      </h5>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          marginBottom: "0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            padding: "4px 10px",
            background: COLORS.primary_color_shade1 || "#B3E5FC",
            borderRadius: "6px",
            border: `1px solid ${COLORS.primary_color_shade3 || "#4FC3F7"}`,
          }}
        >
          <i
            className="fa fa-graduation-cap"
            style={{ color: COLORS.primary_color_shade8 || "#0277BD", fontSize: "11px" }}
          />
          <span
            style={{
              fontSize: "12px",
              fontWeight: "600",
              color: COLORS.black8 || "#333333",
            }}
          >
            {classDetails.class_name || "N/A"} - {classDetails.section_name || "N/A"}
          </span>
        </div>
      </div>
    </div>
  );
});

const AmountCards = React.memo(({ totalDue, totalSettled, totalPaid, currentDue, isSettled }) => {
  const cards = [
    {
      label: "Old Due",
      value: totalDue,
      gradient: `linear-gradient(135deg, ${COLORS.orange5 || "#FF9800"} 0%, ${COLORS.orange7 || "#F57C00"} 100%)`,
      shadow: `${COLORS.orange3}50`,
    },
    {
      label: "Settled",
      value: totalSettled,
      gradient: `linear-gradient(135deg, ${COLORS.blue5 || "#2196F3"} 0%, ${COLORS.blue7 || "#1976D2"} 100%)`,
      shadow: `${COLORS.blue3}50`,
    },
    {
      label: "Paid",
      value: totalPaid,
      gradient: `linear-gradient(135deg, ${COLORS.green3 || "#38B503"} 0%, ${COLORS.green4 || "#2C8D02"} 100%)`,
      shadow: `${COLORS.green2}50`,
    },
    {
      label: "Current Due",
      value: currentDue,
      gradient: isSettled
        ? `linear-gradient(135deg, ${COLORS.green3 || "#38B503"} 0%, ${COLORS.green4 || "#2C8D02"} 100%)`
        : `linear-gradient(135deg, ${COLORS.red5 || "#f44336"} 0%, ${COLORS.red7 || "#d32f2f"} 100%)`,
      shadow: isSettled ? `${COLORS.green2}50` : `${COLORS.red3}50`,
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        gap: "8px",
        flexWrap: "wrap",
        flexShrink: 0,
      }}
    >
      {cards.map((card, idx) => (
        <div
          key={idx}
          style={{
            ...amountCardStyle,
            background: card.gradient,
            boxShadow: `0 2px 6px ${card.shadow}`,
          }}
        >
          <p
            style={{
              margin: "0 0 3px 0",
              fontSize: "10px",
              opacity: 0.9,
              textTransform: "uppercase",
            }}
          >
            {card.label}
          </p>
          <h4 style={{ margin: 0, fontSize: "16px", fontWeight: "700" }}>
            ₹{card.value.toLocaleString()}
          </h4>
        </div>
      ))}
    </div>
  );
});

const AddMoreButton = React.memo(() => {
  return (
    <div style={{ marginTop: "24px" }}>
      <Link to="/AdminMain/AddOldDue" style={{ textDecoration: "none" }}>
        <button
          style={{
            width: "100%",
            padding: "16px 24px",
            fontSize: "18px",
            fontWeight: "700",
            background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#039BE5"} 0%, ${COLORS.primary_color_shade8 || "#0277BD"} 100%)`,
            color: COLORS.white,
            border: "none",
            borderRadius: "12px",
            cursor: "pointer",
            boxShadow: `0 4px 12px ${COLORS.primary_color_shade3}60`,
            transition: "all 0.3s ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = `0 6px 16px ${COLORS.primary_color_shade4}80`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "";
            e.currentTarget.style.boxShadow = `0 4px 12px ${COLORS.primary_color_shade3}60`;
          }}
        >
          <i className="fa fa-plus-circle" />
          Add More Old Due
        </button>
      </Link>
    </div>
  );
});

export default class OldDueList extends React.Component {
  render() {
    return (
      <Router>
        <Route path="/AdminMain/OldDueList/:tag" component={OldDueListContent} />
        <Route
          path="/AdminMain/OldDueCollection/:student_id/:session_id"
          component={OldDueCollection}
        />
        <Route path="/AdminMain/AddOldDue" component={AddOldDue} />
      </Router>
    );
  }
}
